import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Pill, Bandage, Leaf, HeartHandshake, Activity, ShieldCheck, BadgeIndianRupee, PhoneCall, Store, ArrowRight, MessageCircle, HeartPulse, Droplet, Wind, Flower2, ShieldPlus, Sparkles, Soup, UploadCloud, Tag, Baby, Dumbbell, Stethoscope, Star } from "lucide-react";
import AddToCartBtn from "../../storefront/AddToCartBtn";
import { SHOP } from "../../storefront/StoreLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ICONS = [
  [/baby/i, Baby],
  [/mother|pregnan|feeding/i, HeartHandshake],
  [/workout|protein|gym|sport/i, Dumbbell],
  [/vitamin|supplement/i, Sparkles],
  [/ayurved|herbal/i, Leaf],
  [/cardiac|heart|bp/i, Activity],
  [/device|monitor|equipment/i, Stethoscope],
  [/pain|first aid|bandage/i, Bandage],
  [/cold|cough|fever/i, Wind],
  [/diabet/i, Droplet],
  [/acidity|gas|stomach|digest/i, Soup],
  [/allergy|skin/i, Flower2],
  [/health|wellness|immunity/i, HeartPulse],
  [/care|hygiene|personal/i, ShieldPlus],
];

const CAT_TINTS = [
  "bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100",
  "bg-sky-50 text-sky-700 group-hover:bg-sky-100",
  "bg-rose-50 text-rose-700 group-hover:bg-rose-100",
  "bg-amber-50 text-amber-700 group-hover:bg-amber-100",
  "bg-teal-50 text-teal-700 group-hover:bg-teal-100",
  "bg-orange-50 text-orange-700 group-hover:bg-orange-100",
];

function catIcon(name) {
  for (const [re, Icon] of CAT_ICONS) if (re.test(name)) return Icon;
  return Pill;
}

const CONCERNS = [
  { title: "Diabetes Care", icon: Droplet, q: "diabetes" },
  { title: "BP & Heart", icon: HeartPulse, q: "cardiac" },
  { title: "Pain Relief", icon: ShieldPlus, q: "pain" },
  { title: "Cold & Cough", icon: Wind, q: "cold" },
  { title: "Acidity & Gas", icon: Soup, q: "acidity" },
  { title: "Allergy", icon: Flower2, q: "allergy" },
  { title: "Antibiotics", icon: Pill, q: "antibiotic" },
  { title: "Vitamins & Wellness", icon: Sparkles, q: "vitamin" },
];

const FAQS = [
  { q: "Online medicine order kaise karu?", a: "Shop page par medicine search karke Add to Cart karein, phir cart mein naam, phone aur address bhar kar order place karein. Hamari team call karke confirm karegi." },
  { q: "Kya payment online karna zaroori hai?", a: "Nahi. Cash on Delivery, UPI on Delivery ya store par pickup ke waqt payment kar sakte hain." },
  { q: "Prescription wali dawai kaise milegi?", a: "Upload Rx page par prescription ka photo bhej dein ya WhatsApp karein — pharmacist verify karke aapko call karega. Scheduled dawaiyan bina valid prescription ke nahi di jaati." },
  { q: "Delivery kitne time mein hoti hai?", a: "Kashipur local area mein same-day delivery. Stock aur timing ke hisaab se pharmacist call par confirm karega." },
  { q: "Kya medicines genuine hain?", a: "Haan — 100%. Hum licensed distributors se hi stock lete hain, har batch ka expiry check hota hai, aur proper bill milta hai." },
];

const TRUST = [
  { icon: ShieldCheck, title: "Licensed & Verified", desc: "Genuine medicines sourced from certified pharma distributors only." },
  { icon: BadgeIndianRupee, title: "Fair ₹ Pricing", desc: "Transparent MRP-based pricing. No hidden or foreign charges." },
  { icon: PhoneCall, title: "Call-to-Confirm", desc: "Har online order se pehle hum call karke confirm karte hain — medicine safety ke liye." },
  { icon: Store, title: "Real Local Store", desc: "Laxmipur Patti, Ward 8, Kashipur — kabhi bhi counter par aakar milein." },
];

export default function StoreHome() {

  const [meds, setMeds] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/medicines`).then(({ data }) => setMeds(data)).catch(() => {});
    axios.get(`${API}/public/categories`).then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const featured = (() => {
    const handpicked = meds.filter((m) => m.in_stock && m.is_featured);
    const rest = meds.filter((m) => m.in_stock && !m.is_featured);
    return [...handpicked, ...rest].slice(0, 8);
  })();
  const deals = meds.filter((m) => m.in_stock && m.mrp > 0 && m.mrp <= 100).slice(0, 12);

  return (
    <div data-testid="store-home-page">
      <section className="max-w-7xl mx-auto px-4 pt-10 pb-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-900 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5">
            <ShieldCheck className="w-3.5 h-3.5" /> Govt. Licensed Pharmacy • Kashipur
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Kashipur Ki Apni <span className="text-emerald-800">Trusted Pharmacy</span>
          </h1>
          <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-xl">
            100% genuine medicines, OTC products, baby care aur wellness — sab real local prices (₹) mein.
            Online order karein, hum call karke confirm karenge aur same-day Kashipur mein deliver karenge.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/store/shop" data-testid="hero-shop-now-cta" className="inline-flex items-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
              Shop Live Stock <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={SHOP.whatsapp} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-order-cta" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp par Order
            </a>
          </div>
          <div className="mt-7 flex flex-wrap gap-2">
            {["100% Genuine Brands", "Licensed Pharmacist", "Cash on Delivery", "Same-day Local Delivery"].map((t) => (
              <span key={t} className="text-xs font-semibold text-emerald-900 bg-white border border-emerald-200 px-3 py-1.5 rounded-full">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative">
          <HeroCarousel />
          <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-white rounded-2xl shadow-xl px-5 py-4 border border-emerald-100 z-10">
            <div className="text-2xl font-extrabold font-display text-emerald-800">100%</div>
            <div className="text-xs text-slate-500 font-medium">Genuine Medicines</div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Shop by Category</h2>
            <span className="text-xs text-slate-400 hidden sm:inline">CRM se live — real stock se linked</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cats.map((c, i) => {
              const Icon = catIcon(c.name);
              const tint = CAT_TINTS[i % CAT_TINTS.length];
              return (
                <Link key={c.name} to={`/store/shop?cat=${encodeURIComponent(c.name)}`} data-testid={`category-tile-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group bg-[#F8FAFC] hover:bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md rounded-2xl p-4 transition-all">
                  <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 transition-colors ${tint}`}>
                    <Icon className="w-6 h-6" />
                  </span>
                  <div className="font-semibold text-slate-900 text-sm leading-snug flex items-center justify-between gap-1">
                    <span className="truncate">{c.name}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.product_count > 0 ? `${c.product_count} products` : "Jald aa raha hai"}
                    {c.children?.length > 0 && ` • ${c.children.length} sub`}
                  </div>
                </Link>
              );
            })}
            {cats.length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-400 text-sm">Categories load ho rahi hain…</div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="concerns-section">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">Browse by Health Concern</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CONCERNS.map((c) => (
            <Link key={c.title} to={`/store/shop?q=${encodeURIComponent(c.q)}`} data-testid={`concern-${c.q}`}
              className="group bg-white border border-slate-200 hover:border-emerald-300 rounded-2xl p-4 text-center transition-all hover:shadow-md">
              <span className="w-12 h-12 mx-auto rounded-full bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                <c.icon className="w-6 h-6 text-emerald-700" />
              </span>
              <div className="font-semibold text-slate-900 text-sm mt-2.5">{c.title}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Live Stock — Available Now</h2>
            <p className="text-sm text-slate-500 mt-1">Seedha hamari dukaan ki inventory se. Jo dikhe, wo available hai.</p>
          </div>
          <Link to="/store/shop" className="text-sm font-semibold text-emerald-800 hover:text-emerald-600 flex items-center gap-1 shrink-0">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {featured.map((m) => (
            <div key={m.id} data-testid={`featured-product-${m.id}`} className="relative bg-white border border-slate-200 rounded-2xl p-4 flex flex-col hover:shadow-lg hover:border-emerald-200 transition-all">
              <Link to={`/store/medicine/${m.id}`} data-testid={`featured-link-${m.id}`} aria-label={`${m.name} details`} className="absolute inset-0 rounded-2xl z-0" />
              {m.image ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${m.image}`} alt={m.name} loading="lazy" className="w-full aspect-square rounded-2xl object-cover mb-3 border border-slate-100" />
              ) : (
                <div className="w-full aspect-square rounded-2xl bg-emerald-50 flex items-center justify-center mb-3">
                  <Pill className="w-10 h-10 text-emerald-600" />
                </div>
              )}
              <div className="font-semibold text-slate-900 text-sm leading-snug flex-1">{m.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{m.generic_name || m.category}</div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono-num font-bold text-slate-900">₹{m.mrp.toFixed(2)}</span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">IN STOCK</span>
              </div>
              <div className="relative z-10"><AddToCartBtn m={m} testid={`featured-add-${m.id}`} /></div>
            </div>
          ))}
          {featured.length === 0 && (
            <div className="col-span-full text-center py-10 text-slate-400 text-sm">Live stock loading… Shop page par poori list dekhein.</div>
          )}
        </div>
      </section>

      {deals.length > 0 && (
        <section className="bg-amber-50 border-y border-amber-100 py-12" data-testid="deals-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Tag className="w-6 h-6 text-amber-600" /> Value Deals under ₹100</h2>
                <p className="text-sm text-slate-500 mt-1">Rozmarra ki zaroori cheezein, budget mein — live stock se.</p>
              </div>
              <Link to="/store/shop" className="text-sm font-semibold text-emerald-800 hover:text-emerald-600 flex items-center gap-1 shrink-0">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
              {deals.map((m) => (
                <div key={m.id} data-testid={`deal-${m.id}`} className="relative shrink-0 w-44 bg-white border border-amber-200 rounded-2xl p-3.5 flex flex-col">
                  <Link to={`/store/medicine/${m.id}`} data-testid={`deal-link-${m.id}`} aria-label={`${m.name} details`} className="absolute inset-0 rounded-2xl z-0" />
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{m.category}</div>
                  <div className="font-semibold text-slate-900 text-sm leading-snug mt-1 flex-1">{m.name}</div>
                  <div className="font-mono-num font-bold text-slate-900 mt-2">₹{m.mrp.toFixed(2)}</div>
                  <div className="relative z-10"><AddToCartBtn m={m} compact testid={`deal-add-${m.id}`} /></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 pb-14">
        <div className="bg-gradient-to-br from-emerald-900 to-emerald-800 rounded-3xl p-8 sm:p-10 text-white grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold">Doctor ki parchi samajh nahi aa rahi?</h3>
            <p className="mt-3 text-emerald-100 text-sm leading-relaxed">
              Prescription ka photo khinch kar seedha WhatsApp kar dein. Hamara registered pharmacist order ready karega aur confirm karne ke liye aapko call karega.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a href={SHOP.whatsapp} target="_blank" rel="noreferrer" data-testid="rx-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-semibold px-6 py-3.5 rounded-full hover:bg-emerald-50 transition-colors">
              <MessageCircle className="w-4 h-4" /> Prescription WhatsApp karein
            </a>
            <Link to="/store/upload-rx" data-testid="rx-upload-cta"
              className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-semibold px-6 py-3.5 rounded-full transition-colors">
              <UploadCloud className="w-4 h-4" /> Upload Prescription
            </Link>
            <a href={`tel:+91${SHOP.phone}`} className="inline-flex items-center gap-2 border border-emerald-300 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-emerald-800 transition-colors">
              <PhoneCall className="w-4 h-4" /> {SHOP.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">Why Choose Trustmeds Pharma</h2>
        <p className="text-sm text-slate-500 mb-6">Kashipur ki apni licensed pharmacy — genuine medicines, fair prices aur personal care, har order par.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TRUST.map((t) => (
            <div key={t.title} className="bg-white border border-slate-200 rounded-2xl p-5">
              <t.icon className="w-7 h-7 text-emerald-700 mb-3" />
              <div className="font-semibold text-slate-900">{t.title}</div>
              <div className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <ReviewsBlock />
      <DoctorSoon />

      <section className="max-w-7xl mx-auto px-4 pb-16" data-testid="faq-section">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">Frequently Asked Questions</h2>
        <div className="space-y-2.5 max-w-3xl">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-2xl px-5 py-4" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-semibold text-slate-900 text-sm list-none flex items-center justify-between">
                {f.q}
                <span className="text-emerald-700 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
              </summary>
              <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}


const HERO_SLIDES = [
  { src: "/store-assets/maam-suit.jpg", alt: "Trustmeds Pharma ki owner suit-salwar mein medical store par" },
  { src: "/store-assets/pharmacist-counter.jpg", alt: "Trustmeds Pharma ki pharmacist POS billing counter par" },
  { src: "/store-assets/store-interior.jpg", alt: "Trustmeds Pharma medical store andar ka view" },
];

function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="rounded-3xl shadow-2xl w-full aspect-[4/3] overflow-hidden relative bg-emerald-50" data-testid="hero-carousel">
        {HERO_SLIDES.map((s, i) => (
          <img key={s.src} src={s.src} alt={s.alt} loading={i === 0 ? "eager" : "lazy"} data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`} />
        ))}
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="hero-dots">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`Photo ${i + 1}`} data-testid={`hero-dot-${i}`}
            className={`h-2.5 rounded-full transition-all ${i === idx ? "w-6 bg-white shadow" : "w-2.5 bg-white/50 hover:bg-white/80"}`} />
        ))}
      </div>
    </>
  );
}

const STORE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReviewsBlock() {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [rName, setRName] = useState("");
  const [rRating, setRRating] = useState(5);
  const [rMsg, setRMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${STORE_API}/public/reviews`).then((r) => r.json()).then(setReviews).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const resp = await fetch(`${STORE_API}/public/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: rName, rating: rRating, message: rMsg }),
      });
      if (!resp.ok) throw new Error();
      setDone(true);
    } catch {
      alert("Review submit nahi ho paya. Dobara try karein.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 pb-16" data-testid="reviews-section">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Customer Reviews</h2>
          <p className="text-sm text-slate-500 mt-1">Kashipur ke customers ka bharosa — unki apni zubaani.</p>
        </div>
        <button onClick={() => { setOpen(!open); setDone(false); }} data-testid="write-review-btn"
          className="inline-flex items-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
          <Star className="w-4 h-4" /> Apna Review Likhein
        </button>
      </div>

      {open && (
        done ? (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center" data-testid="review-thanks">
            <div className="font-bold text-emerald-900">Shukriya! Aapka review mil gaya.</div>
            <div className="text-xs text-emerald-700 mt-1">Store approve karte hi ye website par dikhega.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="mb-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="review-form">
            <div className="flex items-center gap-1.5" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <button type="button" key={s} onClick={() => setRRating(s)} data-testid={`review-star-${s}`} aria-label={`${s} star`}>
                  <Star className={`w-7 h-7 transition-colors ${s <= rRating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                </button>
              ))}
            </div>
            <input required value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Aapka naam *" data-testid="review-name-input"
              className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <textarea required value={rMsg} onChange={(e) => setRMsg(e.target.value)} placeholder="Aapka experience kaisa raha? *" rows={3} data-testid="review-message-input"
              className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <button type="submit" disabled={sending} data-testid="review-submit-btn"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
              {sending ? "Bhej rahe hain…" : "Review Bhejein"}
            </button>
          </form>
        )
      )}

      {reviews.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                ))}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">"{r.message}"</p>
              <div className="text-xs font-semibold text-slate-900 mt-3">— {r.name}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="reviews-empty">
          Abhi koi review nahi — pehle review dene wale banein!
        </div>
      )}
    </section>
  );
}

function DoctorSoon() {
  return (
    <section className="max-w-7xl mx-auto px-4 pb-16" data-testid="doctor-consult-section">
      <div className="bg-emerald-900 rounded-3xl p-8 sm:p-10 text-white flex flex-col sm:flex-row items-center gap-6">
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center shrink-0">
          <Stethoscope className="w-8 h-8 text-emerald-200" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-300 mb-1">Coming Soon</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Online Doctor Consultation</h2>
          <p className="text-emerald-100/80 text-sm mt-2 max-w-xl leading-relaxed">
            Ghar baithe experienced doctors se salah lein — aur prescribed medicines seedha Trustmeds Pharma se same-day delivery ke saath. Jald aa raha hai.
          </p>
        </div>
        <span className="bg-white/10 border border-white/20 text-white text-xs font-bold px-4 py-2 rounded-full shrink-0" data-testid="doctor-coming-soon-badge">JALD AA RAHA HAI</span>
      </div>
    </section>
  );
}
